import { IsOptionSetItemDisabled } from "components/controls/OptionsetControl";
import { EValidationSeverity } from "components/controls/ValidationView/ValidationView.type";
import { EControlType } from "components/ControlWrapper";
import { IGCDataLight } from "global/Reducer/dataSlice";
import { BuildVisibilityScope } from "global/Visibility/GlobalVisibility";
import { IVisibilityScope } from "global/Visibility/IVisibilityScope.type";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import { bcbs_config_attribute, bcbs_config_masterattribute } from "model/CRM";
import { Ibcbs_config_attribute_extended } from "model/CRM.type";
import { IBenefit, IFlexibleField, IFlexibleFieldSet, IGroupNumber, INonStandard, IPlan, TCategoryEntity } from "model/FlexibleAttributeModel";
import { EvaluateFieldDisability } from "views/BlueRelationship/FlexibleFieldView";

// import type * as IXrmTypesExtended from "model/CRM.type";
declare namespace IXrmTypesExtended {
    type Ibcbs_config_optionsetitem_extended = import('model/CRM.type').Ibcbs_config_optionsetitem_extended;
}

interface IPScope {
    scope: 'benefit'
    origins: IBenefit[]
}

interface IFlexibleFieldSetScope {
    scope: 'attributeSets'
    origins: IFlexibleFieldSet[]

}

// export type TDataEntity = {
//     id: string,
//     name?: string,
//     tiers?: { id: string, name: string }[],

//     fields: IFlexibleField[],

//     // benefit:
//     // grandFathered
//     // status
//     // fundingType
//     // product

//     // group numbers:
//     // ...

//     attributeSets?: IFlexibleFieldSet[],

//     nonStandards?: INonStandard[],
//     // sectionRef
//     // referenceOnly

//     deactivated?: boolean
// };
type TScope = 'Benefit' | 'Plan' | 'Group Number' | 'Fees and Premium' | 'Stop Loss' | 'Accounts Receivable' | 'Attribute Set' | 'Non-Standard' | 'N/A' | string;


export type TDataEntity = INonStandard & IFlexibleFieldSet & IBenefit;

interface IValidationEngineResult {
    scope: string[];
    bcbs_config_masterattribute: bcbs_config_masterattribute;
    bcbs_config_attribute: bcbs_config_attribute;
    type: EValidationSeverity;
    tierRefName?: string;
}

interface IValidationEngineExpireResult {
    scope: string[];
    bcbs_config_masterattribute: bcbs_config_masterattribute;
    bcbs_config_attribute: bcbs_config_attribute;
    type: EValidationSeverity;
    optionSetItem: IXrmTypesExtended.Ibcbs_config_optionsetitem_extended;
}

class ValidationEngine {
    originState: IGCDataLight;
    requiredFieldsResults: IValidationEngineResult[] = [];
    expiredFieldsResults: IValidationEngineExpireResult[] = [];
    isMnilSnapshot: boolean;
    changeTypeValue: number;

    constructor(originState: IGCDataLight) {
        this.originState = originState;
        this.isMnilSnapshot = originState?.json?.MNIL?.value ?? false;
        this.changeTypeValue = originState.json?.changeType?.value ?? IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value;
    }

    // this maybe a long running process
    // we need to run it in iterations of the event loop to prevent blocking the UI
    // TODO error handling and failure recovery
    async processAsync() {

        const scope: TScope[] = ["Group Configuration"];

        const visiblityScope = BuildVisibilityScope(
            null,
            this.originState.json,
            null,
            null
        );;

        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    [this.originState.json as any],
                    [],
                    visiblityScope,
                );

                resolve(mergedEntities);
            });
        });

        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    this.originState.json.plans,
                    [...scope, "Plan"],
                    visiblityScope,
                );

                resolve(mergedEntities);
            });
        });

        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    this.originState.json.benefits,
                    [...scope, "Benefit"],
                    visiblityScope,
                );

                resolve(mergedEntities);
            });
        });

        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    this.originState.json.groupStructure.groupNumbers,
                    [...scope, "Group Number"],
                    visiblityScope,
                );
                resolve(mergedEntities);
            });
        });

        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    this.originState.json.feesAndPremiums,
                    [...scope, "Fees and Premium"],
                    visiblityScope,
                );

                resolve(mergedEntities);
            });
        });

        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    this.originState.json.stopLoss,
                    [...scope, "Stop Loss"],
                    visiblityScope,
                );

                resolve(mergedEntities);
            });
        });



        // run in the next iteration of the event loop, a chance to run context tasks
        await new Promise((resolve) => {
            setImmediate(() => {
                const mergedEntities = this.processEntity(
                    this.originState.json.claimsARs,
                    [...scope, "Accounts Receivable"],
                    visiblityScope,
                );

                resolve(mergedEntities);
            });
        });
    }

    private processEntity(
        entities: TCategoryEntity[],
        scope: TScope[],
        parentVisibilityScope: IVisibilityScope
    ) {
        // TODO fix the typing
        // temporary measure to have a typing, once they all share the same interface we will use ICategory
        //  IBenefit[] |
        //     IFlexibleFieldSet[] |
        //     INonStandard[] |
        //     IGroupNumber[] |
        //     IFeesAndPremiums[] |
        //     IStopLoss[] |
        //     IGroupConfigurationData[]

        (entities).forEach((eachEntity) => {

            const isBenefit = scope[scope.length - 1] === 'Benefit';
            const isPlan = scope[scope.length - 1] === 'Plan';
            const isGroupNumber = scope[scope.length - 1] === 'Group Number';

            const visibilityScope = BuildVisibilityScope(
                parentVisibilityScope,
                this.originState.json,
                isBenefit ? (eachEntity as IBenefit) : null,
                isPlan ? (eachEntity as IPlan) : null,
                isGroupNumber ? (eachEntity as IGroupNumber) : null
            );

            if (eachEntity.deactivated)
                return;

            if (eachEntity.fields)
                this.processFields(
                    eachEntity,
                    eachEntity.fields,
                    [...scope, eachEntity.name ?? 'N/A'],
                    visibilityScope,
                );

            if (eachEntity.attributeSets && eachEntity.attributeSets.length > 0)
                this.processEntity(
                    eachEntity.attributeSets,
                    [...scope, eachEntity.name ?? 'N/A', 'Attribute Set'],
                    visibilityScope,
                );

            if (eachEntity.nonStandards && eachEntity.nonStandards.length > 0)
                this.processEntity(eachEntity.nonStandards,
                    [...scope, eachEntity.name ?? 'N/A', 'Non-Standard'],
                    visibilityScope,
                );
        });
    }

    private processFields(
        eachEntity: TCategoryEntity,
        fields: IFlexibleField[],
        scope: TScope[],
        visibilityScope: IVisibilityScope
    ) {

        fields.forEach((eachField, index) => {

            const config = this.originState.config;
            const configRefId = eachField.configRef.id;

            const isHidden = (eachField.visibility ?? 0) > 0;

            if (isHidden)
                return;

            const bcbs_config_attribute = config.cache.map_bcbs_config_attribute[configRefId] as Ibcbs_config_attribute_extended;

            // const bcbs_config_masterattribute = bcbs_config_attribute
            //     ? config.cache.map_bcbs_config_masterattribute[bcbs_config_attribute._bcbs_config_masterattributeid_value!]
            //     : null;

            if (!bcbs_config_attribute) {
                console.warn("bcbs_config_attribute not found", configRefId);
                return;
            }

            let bcbs_config_masterattributeid = bcbs_config_attribute.bcbs_config_attributeid!;

            // MNIL and Regular snapshots are evaluated differently
            const validationCode = this.isMnilSnapshot
                ? bcbs_config_attribute.bcbs_mnil_validation
                : bcbs_config_attribute.bcbs_requiredforvalidationcode

            // we try to find the key using the master attribute id
            bcbs_config_masterattributeid = bcbs_config_attribute._bcbs_config_masterattributeid_value!;

            const bcbs_config_masterattribute = config.cache.map_bcbs_config_masterattribute[bcbs_config_masterattributeid];

            if (!bcbs_config_masterattribute) {
                console.warn("bcbs_config_masterattribute not found", bcbs_config_masterattributeid);
                return;
            }

            // determine if the fields change type matches the configuration's change type
            let fieldDisabled = EvaluateFieldDisability(this.changeTypeValue, bcbs_config_attribute);

            // if (bcbs_config_attribute.bcbs_iscancellation) {
            //     disabled = disabled || bcbs_config_attribute.bcbs_iscancellation === false;
            // }

            // bcbs_requiredforvalidationcode	
            // Name	Value
            // Error	808900000
            // Warning Only	808900002

            eachField.values.forEach((eachValue) => {
                if (
                    (!eachValue.value) ||
                    (
                        eachValue.value &&
                        eachValue.value.type === 'string' &&
                        !eachValue.value.value
                    )
                ) {

                    if (!fieldDisabled)
                        if (validationCode === IXrmOptions.bcbs_config_attribute.bcbs_requiredforvalidationcode["Error(808900000)"].value) {
                            this.requiredFieldsResults.push({
                                scope: scope,
                                bcbs_config_masterattribute: bcbs_config_masterattribute,
                                bcbs_config_attribute: bcbs_config_attribute,
                                type: EValidationSeverity.error,
                                tierRefName: eachValue.tierRef?.name,
                            });
                        } else if (validationCode === IXrmOptions.bcbs_config_attribute.bcbs_requiredforvalidationcode["Warning Only(808900002)"].value) {
                            this.requiredFieldsResults.push({
                                scope: scope,
                                bcbs_config_masterattribute: bcbs_config_masterattribute,
                                bcbs_config_attribute: bcbs_config_attribute,
                                type: EValidationSeverity.warn,
                                tierRefName: eachValue.tierRef?.name
                            });
                        }
                }

                if (eachValue.value &&
                    eachValue.value.type === 'multioptionset' &&
                    (bcbs_config_masterattribute.bcbs_attributetypecode as EControlType) === IXrmOptions.bcbs_config_masterattribute.bcbs_attributetypecode["Multiselect Option Set(808900016)"].value
                ) {

                    eachValue.value.options.forEach((eachOption) => {

                        const optionSetItem = config.cache.map_bcbs_config_optionsetitems[eachOption.id] as IXrmTypesExtended.Ibcbs_config_optionsetitem_extended;
                        if (optionSetItem) {

                            const isOptionSetItemDisabled = IsOptionSetItemDisabled(optionSetItem, visibilityScope);

                            // Validation Edit - Display hard edit upon submit for Expired Option Set Items
                            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/676172
                            if (isOptionSetItemDisabled) {
                                this.expiredFieldsResults.push({
                                    scope: scope,
                                    bcbs_config_masterattribute: bcbs_config_masterattribute,
                                    bcbs_config_attribute: bcbs_config_attribute,
                                    type: EValidationSeverity.error,
                                    optionSetItem: optionSetItem,
                                });
                            }
                        }
                    });


                }

                if (eachValue.value &&
                    eachValue.value.type === 'entity' &&
                    (bcbs_config_masterattribute.bcbs_attributetypecode as EControlType) === IXrmOptions.bcbs_config_masterattribute.bcbs_attributetypecode["Option Set(808900007)"].value
                ) {

                    const optionSetItem = config.cache.map_bcbs_config_optionsetitems[eachValue.value.id] as IXrmTypesExtended.Ibcbs_config_optionsetitem_extended;
                    if (optionSetItem) {

                        const isOptionSetItemDisabled = IsOptionSetItemDisabled(optionSetItem, visibilityScope);

                        // Validation Edit - Display hard edit upon submit for Expired Option Set Items
                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/676172
                        if (isOptionSetItemDisabled) {

                            this.expiredFieldsResults.push({
                                scope: scope,
                                bcbs_config_masterattribute: bcbs_config_masterattribute,
                                bcbs_config_attribute: bcbs_config_attribute,
                                type: EValidationSeverity.error,
                                optionSetItem: optionSetItem,
                            });
                        }
                    }

                }


            });


        });

    }
}

export {
    ValidationEngine as default
};

